import Dialog from "@components/Dialog"
import { AppNode } from "@features/builder/graph/types"
import BulkReplaceSection from "@features/builder/BulkNodesEditDialog/BulkReplaceSection"
import { ReplaceField } from "@features/builder/BulkNodesEditDialog/FieldsRadioGroup"
import FilterSection from "@features/builder/BulkNodesEditDialog/FilterSection"
import BulkNodesEditForm, {
  BulkNodesEditFormRef,
  BulkNodesEditFormValues,
} from "@features/builder/BulkNodesEditDialog/BulkNodesEditForm/BulkNodesEditForm"
import { Box, Button, DialogActions, FormHelperText, Grid, Typography } from "@mui/material"
import { useNodes, useReactFlow } from "@xyflow/react"
import { useEffect, useRef, useState } from "react"
import { useGraph } from "@features/builder/graph/graphAtoms"
import { useSocketIO } from "@features/builder/hooks/socketIOHooks"
import { SubmitErrorHandler, SubmitHandler } from "react-hook-form"
import { scrollToFirstFieldError } from "@utils/form"
import { applyFormValuesToNode } from "@features/builder/BulkNodesEditDialog/BulkNodesEditForm/helpers"
import { getChildrenNodes, getNodeLink } from "@features/builder/helpers/nodes"
import { updateParentParamsOfChildNode } from "@features/builder/helpers/update-children-node"
import { isEqual } from "lodash"
import { enqueueSnackbar } from "notistack"
import { waitForNextPaint } from "@utils/dom"
import { useGetLinkedNode } from "@features/builder/hooks/useGetLinkedNode"

type BulkNodesEditDialogProps = {
  onClose: () => void
}

export const BULK_NODES_EDIT_DIALOG_ACTIONS_ID = "bulk-nodes-edit-dialog-actions"
const ITERATIONS_BEFORE_PAINT = 25

const BulkNodesEditDialog = (props: BulkNodesEditDialogProps) => {
  const allNodes = useNodes<AppNode>()
  const { getLinkedNode } = useGetLinkedNode()
  const { updateNode } = useReactFlow<AppNode>()
  const [filteredNodes, setFilteredNodes] = useState<AppNode[]>(allNodes)
  const [globalErrorMessages, setGlobalErrorMessages] = useState<string[]>([])

  const formRef = useRef<BulkNodesEditFormRef>(null)
  const socket = useSocketIO()

  const { isLocked, graphId, remotelyLockedBy } = useGraph()

  useEffect(() => {
    if (!graphId) return

    // on enter bulk edit dialog, acquire graph lock if not yet acquired
    if (!remotelyLockedBy) {
      socket.emit("graph_lock_acquire", { id: graphId })
    }

    // on exit dialog or page / browser tab close, release graph lock
    const releaseGraphLock = () => {
      if (!remotelyLockedBy) return
      socket.emit("graph_lock_release", { id: graphId })
    }

    window.addEventListener("beforeunload", releaseGraphLock)
    return () => {
      releaseGraphLock()
      window.removeEventListener("beforeunload", releaseGraphLock)
    }
  }, [socket, graphId, remotelyLockedBy])

  const handleReplace = (searchValue: string, replaceValue: string, field: ReplaceField) => {
    formRef.current?.applyReplace(searchValue, replaceValue, field)
  }

  const handleSubmit: SubmitHandler<BulkNodesEditFormValues> = async (values) => {
    const selectedRows = Object.values(values.nodes).filter((row) => row.isSelected)
    const allNodesUpdatedMap = new Map(allNodes.map((node) => [node.id, node]))

    for (const [selectedRowIndex, row] of selectedRows.entries()) {
      if (selectedRowIndex % ITERATIONS_BEFORE_PAINT === 0) {
        // "waitForNextPaint" is used to give some time for the UI to update and show the spinner
        // as this submit for huge number of nodes may take a while and user may think the app is frozen for few seconds
        await waitForNextPaint()
      }

      const node = allNodesUpdatedMap.get(row.id)
      if (!node) continue

      const updatedNode = applyFormValuesToNode(row, node)
      allNodesUpdatedMap.set(updatedNode.id, updatedNode)
      const updatedChildrenNodes: AppNode[] = getChildrenNodes(
        updatedNode,
        Array.from(allNodesUpdatedMap.values()),
      )
      const linkedNode = await getLinkedNode(getNodeLink(updatedNode))

      updatedChildrenNodes.forEach((childNode) => {
        const updatedNodes = updateParentParamsOfChildNode(
          childNode,
          node,
          updatedNode,
          Array.from(allNodesUpdatedMap.values()),
          linkedNode,
        )
        updatedNodes.forEach((node) => allNodesUpdatedMap.set(node.id, node))
      })
    }

    for (const [allNodeIndex, node] of allNodes.entries()) {
      if (allNodeIndex % ITERATIONS_BEFORE_PAINT === 0) {
        await waitForNextPaint()
      }

      const updatedNode = allNodesUpdatedMap.get(node.id)
      const hasChanged = !isEqual(node, updatedNode)

      if (updatedNode && hasChanged) updateNode(node.id, updatedNode, { replace: true })
    }

    enqueueSnackbar({ message: "Changes saved", variant: "success" })

    // reset filtered nodes and the form based on them
    const filteredNodesUpdated = filteredNodes.map(
      (node) => allNodesUpdatedMap.get(node.id) ?? node,
    )
    setFilteredNodes(filteredNodesUpdated)
    formRef.current?.reset({ keepDirtyChanges: false, nodes: filteredNodesUpdated })
  }

  const handleSubmitError: SubmitErrorHandler<BulkNodesEditFormValues> = (errors) => {
    const errorMessages: string[] = []

    const globalError = errors.nodes?.root?.message ?? ""
    if (globalError) errorMessages.push(globalError)

    const invalidNodesIds = Object.keys(errors.nodes ?? {}).filter((nodeId) => nodeId !== "root")
    const invalidNodesNumericOrder = filteredNodes.reduce<number[]>((acc, node, index) => {
      if (invalidNodesIds.includes(node.id)) acc.push(index + 1)

      return acc
    }, [])

    // We are virtiualising the rows, so it's very likely errored rows are not even rendered, so scrolling into them won't work.
    // We need a fallback way to showing the user which rows are invalid, so we will show numbers of the invalid rows
    if (invalidNodesNumericOrder.length > 0) {
      errorMessages.push(`Invalid rows numbers: ${invalidNodesNumericOrder.join(", ")}`)
    }

    setGlobalErrorMessages(errorMessages)
    scrollToFirstFieldError()
  }

  const handleFilter = (updatedFilteredNodes: AppNode[]) => {
    setFilteredNodes(updatedFilteredNodes)
    formRef.current?.reset({ keepDirtyChanges: true, nodes: updatedFilteredNodes })
  }

  return (
    <Dialog
      open
      onClose={props.onClose}
      title="Bulk nodes edit"
      maxWidth={false}
      fullWidth
      actionsComponent={
        <DialogActions id={BULK_NODES_EDIT_DIALOG_ACTIONS_ID} sx={{ width: "100%" }}>
          <Box sx={{ flexGrow: 1 }}>
            {globalErrorMessages.map((error, index) => (
              <FormHelperText error key={`${error}-${index}`}>
                - {error}
              </FormHelperText>
            ))}
          </Box>

          <Button onClick={props.onClose} color="error" variant="contained">
            Close
          </Button>
        </DialogActions>
      }
    >
      <Grid container justifyContent="center" spacing={1}>
        <Grid item container direction="column" spacing={2} wrap="nowrap" xs={12} md={8} lg={6}>
          <Grid item>
            <FilterSection onFilter={handleFilter} />
          </Grid>

          <Grid item>
            <BulkReplaceSection onReplace={handleReplace} />
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <Typography>
            Matching nodes: <strong>{filteredNodes.length}</strong> /{" "}
            <strong>{allNodes.length}</strong>
          </Typography>
          <BulkNodesEditForm
            ref={formRef}
            onSubmit={handleSubmit}
            onSubmitError={handleSubmitError}
            nodes={filteredNodes}
            disabled={isLocked}
          />
        </Grid>
      </Grid>
    </Dialog>
  )
}

export default BulkNodesEditDialog
